import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { useProgramConfig } from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { EnabledChip, ProgramModelChip } from './chips';
import { SectionHeader } from './primitives';
import { ProgramModelDialog } from './ProgramModelDialog';

interface ProgramModelCardProps {
  canView: boolean;
  canManage: boolean;
  onToast: (message: string) => void;
}

/** Shows + edits the tenant's loyalty programme working model (BONUS / DISCOUNT / NONE). */
export function ProgramModelCard({ canView, canManage, onToast }: ProgramModelCardProps) {
  const { t } = useTranslation('loyalty');
  const config = useProgramConfig({ enabled: canView });
  const [open, setOpen] = useState(false);

  return (
    <Paper variant="outlined">
      <SectionHeader title={t('programModelCard.title')} />
      <Box sx={{ p: 2.5 }}>
        {config.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('programModelCard.loading')}
            </Typography>
          </Stack>
        ) : config.isError ? (
          <Alert severity="error">{t('programModelCard.loadError')}</Alert>
        ) : config.data ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <ProgramModelChip model={config.data.model} />
              <EnabledChip enabled={config.data.active} />
              {config.data.model === 'BONUS' && config.data.bonusActivationDelayDays ? (
                <Typography variant="body2" color="text.secondary">
                  {t('programModelCard.activationDelay', { count: config.data.bonusActivationDelayDays })}
                </Typography>
              ) : null}
              {config.data.updatedAt ? (
                <Typography variant="caption" color="text.secondary">
                  {t('programModelCard.updatedAt', { date: formatDateTime(config.data.updatedAt) })}
                </Typography>
              ) : null}
            </Stack>
            {canManage ? (
              <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => setOpen(true)}>
                {t('programModelCard.editAction')}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Box>

      <ProgramModelDialog
        open={open}
        config={config.data ?? null}
        onClose={() => setOpen(false)}
        onDone={onToast}
      />
    </Paper>
  );
}
